import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';

const ReturnOnInvestmentCalculator = () => {
  const [initialInvestment, setInitialInvestment] = useState('');
  const [finalValue, setFinalValue] = useState('');
  const [roi, setRoi] = useState('');
  const [graphData, setGraphData] = useState(null);

  const calculateRoi = () => {
    if (!initialInvestment || !finalValue) {
      alert('Please fill in all fields.');
      return;
    }

    const initial = parseFloat(initialInvestment);
    const final = parseFloat(finalValue);

    if (initial <= 0) {
      alert('Initial investment must be greater than zero.');
      return;
    }

    const roiValue = ((final - initial) / initial) * 100;
    setRoi(roiValue.toFixed(2));

    // Generate data for the graph
    const years = 5; // Assuming a 5-year period for visualization
    const data = [];
    for (let i = 0; i <= years; i++) {
      const value = initial + (final - initial) * (i / years);
      data.push(value.toFixed(2));
    }

    const labels = Array.from({ length: years + 1 }, (_, i) => `${i} year${i > 1 ? 's' : ''}`);
    const dataset = {
      label: 'Investment Growth',
      data: data,
      borderColor: 'rgba(75,192,192,1)',
      backgroundColor: 'rgba(75,192,192,0.4)',
      fill: true,
    };

    const chartData = {
      labels: labels,
      datasets: [dataset],
    };

    const chartOptions = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Investment Growth Over Time',
        },
      },
    };

    setGraphData({ chartData, chartOptions });
  };

  return (
    <div className="container mx-auto py-10 px-4">
      <Helmet>
        <title>Return on Investment Calculator</title>
        <meta name="description" content="A simple Return on Investment calculator using Tailwind CSS and React" />
      </Helmet>
      <h1 className="text-4xl font-bold text-center mb-8 text-indigo-600">Return on Investment Calculator</h1>
      <form className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4 max-w-md mx-auto">
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="initialInvestment">
            Initial Investment
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
            id="initialInvestment"
            type="number"
            placeholder="Enter initial investment"
            value={initialInvestment}
            onChange={(e) => setInitialInvestment(e.target.value)}
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="finalValue">
            Final Value of Investment
          </label>
          <input
            className="shadow appearance-none border rounded w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
            id="finalValue"
            type="number"
            placeholder="Enter final value of investment"
            value={finalValue}
            onChange={(e) => setFinalValue(e.target.value)}
          />
        </div>
        <button
          className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline"
          type="button"
          onClick={calculateRoi}
        >
          Calculate ROI
        </button>
      </form>
      {roi && (
        <div className="mt-6 text-center">
          <p className="text-3xl font-bold text-green-600">Return on Investment: {roi}%</p>
        </div>
      )}
      {graphData && (
        <div className="mt-6">
          <Line data={graphData.chartData} options={graphData.chartOptions} />
        </div>
      )}
      <div className="mt-10">
        <h2 className="text-3xl font-bold mb-4 text-indigo-600">How to Use</h2>
        <p className="text-gray-700 text-lg">
          1. Enter the initial investment amount.<br />
          2. Enter the final value of the investment.<br />
          3. Click the "Calculate ROI" button to see the return on investment percentage.
        </p>
      </div>
      <div className="mt-10">
        <h2 className="text-3xl font-bold mb-4 text-indigo-600">Features</h2>
        <ul className="list-disc list-inside text-gray-700 text-lg">
          <li>Calculate ROI based on initial investment and final value.</li>
          <li>View the ROI percentage.</li>
          <li>Visualize the investment growth over time with a graph.</li>
        </ul>
      </div>
      <div className="mt-10">
        <h2 className="text-3xl font-bold mb-4 text-indigo-600">Benefits</h2>
        <ul className="list-disc list-inside text-gray-700 text-lg">
          <li>Evaluate the profitability of your investments.</li>
          <li>Make informed decisions about future investments.</li>
          <li>Visualize the impact of different investment strategies.</li>
        </ul>
      </div>
    </div>
  );
};

export default ReturnOnInvestmentCalculator;